import { DatePickerProps } from '..';
import { Omit } from '@material-ui/core';
import { IUtils } from '@date-io/core/IUtils';
import { ParsableDate } from '../constants/prop-types';
import { BasePickerProps } from '../typings/BasePicker';

const replaceAt = (str: string, index: number, replacement: string) => {
  replacement = replacement || '';
  return str.substr(0, index) + replacement + str.substr(index + replacement.length);
};

export const getDisplayDate = (
  value: ParsableDate,
  format: string,
  utils: IUtils<any, any>,
  isEmpty: boolean,
  { invalidLabel, emptyLabel, labelFunc }: Omit<BasePickerProps, 'value' | 'onChange'>
) => {
  const date = utils.date(value);
  if (labelFunc) {
    return labelFunc(isEmpty ? null : date, invalidLabel!);
  }

  if (isEmpty) {
    return emptyLabel || '';
  }

  return utils.isValid(date) ? utils.formatByString(date, format) : invalidLabel!;
};

export interface BaseValidationProps {
  /**
   * Message, appearing when date cannot be parsed
   * @default 'Invalid Date Format'
   */
  invalidDateMessage?: React.ReactNode;
}

export interface DateValidationProps extends BaseValidationProps {
  /**
   * Error message, shown if date is less then minimal date
   * @default 'Date should not be before minimal date'
   */
  minDateMessage?: React.ReactNode;
  /**
   * Error message, shown if date is more then maximal date
   * @default 'Date should not be after maximal date'
   */
  maxDateMessage?: React.ReactNode;
}

const getComparisonMaxDate = (utils: IUtils<any, any>, strictCompareDates: boolean, date: Date) => {
  if (strictCompareDates) {
    return date;
  }

  return utils.endOfDay(date);
};

const getComparisonMinDate = (utils: IUtils<any, any>, strictCompareDates: boolean, date: Date) => {
  if (strictCompareDates) {
    return date;
  }

  return utils.startOfDay(date);
};

export const validate = (
  value: ParsableDate,
  utils: IUtils<any, any>,
  {
    maxDate,
    minDate,
    disablePast,
    disableFuture,
    maxDateMessage,
    minDateMessage,
    invalidDateMessage,
    strictCompareDates,
  }: Omit<DatePickerProps, 'views' | 'openTo'> // DateTimePicker doesn't support
): React.ReactNode => {
  const parsedValue = utils.date(value);

  // if null - do not show error
  if (value === null) {
    return '';
  }

  if (!utils.isValid(value)) {
    return invalidDateMessage;
  }

  if (
    maxDate &&
    utils.isAfter(
      parsedValue,
      getComparisonMaxDate(utils, !!strictCompareDates, utils.date(maxDate))
    )
  ) {
    return maxDateMessage;
  }

  if (
    disableFuture &&
    utils.isAfter(parsedValue, getComparisonMaxDate(utils, !!strictCompareDates, utils.date()))
  ) {
    return maxDateMessage;
  }

  if (
    minDate &&
    utils.isBefore(
      parsedValue,
      getComparisonMinDate(utils, !!strictCompareDates, utils.date(minDate))
    )
  ) {
    return minDateMessage;
  }
  if (
    disablePast &&
    utils.isBefore(parsedValue, getComparisonMinDate(utils, !!strictCompareDates, utils.date()))
  ) {
    return minDateMessage;
  }

  return '';
};

export function pick12hOr24hFormat(
  userFormat: string | undefined,
  ampm: boolean | undefined = true,
  formats: { '12h': string; '24h': string }
) {
  if (userFormat) {
    return userFormat;
  }

  return ampm ? formats['12h'] : formats['24h'];
}

export function makeMaskFromFormat(format: string, numberMaskChar: string) {
  return format.replace(/[a-z]/gi, numberMaskChar);
}

export const maskedDateFormatter = (mask: string, numberMaskChar: string) => (
  value: string,
  oldValue?: string,
  currentCaretPosition?: number
) => {
  const refuse = /[^\d\w_]+/gi;

  if (!value || value === '') {
    return value;
  }

  if (!oldValue) {
    oldValue = '';
  }

  if (value == oldValue) {
    return value;
  }

  if (value.length < oldValue.length && currentCaretPosition != null) {
    value =
      value.slice(0, currentCaretPosition) +
      '_' +
      value.slice(currentCaretPosition, oldValue.length);
  }

  let refusedOldValue = oldValue.replace(refuse, '');
  let refusedNewValue = value.replace(refuse, '');
  let refusedMaskSymbols = mask.replace(refuse, '');

  const minLength =
    refusedOldValue.length > refusedNewValue.length
      ? refusedNewValue.length
      : refusedOldValue.length;

  let startPos = 0;
  let endPosShift = 0;
  while (startPos < minLength) {
    if (refusedNewValue[startPos] == refusedOldValue[startPos]) {
      startPos++;
    } else {
      break;
    }
  }

  while (
    endPosShift < minLength &&
    startPos < refusedNewValue.length - endPosShift &&
    startPos < refusedOldValue.length - endPosShift
  ) {
    if (
      refusedNewValue[refusedNewValue.length - endPosShift - 1] ==
      refusedOldValue[refusedOldValue.length - endPosShift - 1]
    ) {
      endPosShift++;
    } else {
      break;
    }
  }

  let endStartPos = refusedNewValue.length - endPosShift;
  endStartPos = endStartPos < startPos ? startPos : endStartPos;

  let firstPart = refusedNewValue.slice(0, startPos);
  let addedPart = refusedNewValue.slice(startPos, endStartPos);
  let endPart = refusedNewValue.slice(endStartPos, refusedNewValue.length);

  let maxSymbolsInEnd = refusedMaskSymbols.length - firstPart.length - addedPart.length;
  while (maxSymbolsInEnd > endPart.length) {
    addedPart += numberMaskChar;
    maxSymbolsInEnd--;
  }
  let entireRefusedString =
    firstPart + addedPart + endPart.slice(endPart.length - maxSymbolsInEnd, endPart.length);

  // Do nothing ?...
  entireRefusedString = entireRefusedString.slice(0, refusedMaskSymbols.length);

  let i = 0;
  let n = 0;
  let result = '';
  while (i < mask.length) {
    const maskChar = mask[i];
    if (maskChar === numberMaskChar && n < entireRefusedString.length) {
      const parsedChar = entireRefusedString[n];
      result += parsedChar;
      n += 1;
    } else {
      result += maskChar;
    }
    i += 1;
  }

  return result;
};
